import * as React from 'react';

import CareerCertificateItem from 'bundles/unified-description-page-common/components/modules/syllabus/CareerCertificateItem';
import EligibleDegreesItem from 'bundles/unified-description-page-common/components/modules/syllabus/EligibleDegreesItem';
import ModuleItemContent from 'bundles/unified-description-page-common/components/modules/syllabus/ModuleItemContent';
import ModuleItemSubtitle from 'bundles/unified-description-page-common/components/modules/syllabus/ModuleItemSubtitle';
import SyllabusItem from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItem';
import SyllabusItemTitle from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemTitle';
import SyllabusModule from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusModule';
import InstructorAndPartnerInfo from 'bundles/unified-description-page-common/components/shared/InstructorAndPartnerInfo';
import TogglableDescription from 'bundles/unified-description-page-common/components/shared/TogglableDescription';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import getGroupedAssessments from 'bundles/unified-description-page-common/utils/getGroupedAssessments';
import getNumberOfInstructorsToShow from 'bundles/unified-description-page-common/utils/getNumberOfInstructorsToShow';
import { COURSE_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getUniqueEmbeddedPartners from 'bundles/unified-description-page-common/utils/getUniqueEmbeddedPartners';
import { getCourseSyllabusTitle } from 'bundles/unified-description-page-common/utils/syllabus';

import _t from 'i18n!nls/xdp';

type Props = {
  pageData: ConsumerCoursePageFieldsFragment | EnterpriseCoursePageFieldsFragment;
  config?: {
    disableCta?: boolean;
    disableDegrees?: boolean;
    disableAceCta?: boolean;
  };
};

const CourseSyllabusModuleRenderer = ({ pageData, config }: Props) => {
  if (!pageData.material?.weeks || !pageData.material.weeks.length) {
    return null;
  }

  return (
    <SyllabusModule
      anchorId={COURSE_NAV_ITEMS.MODULES}
      title={getCourseSyllabusTitle(pageData.material.weeks.length)}
      description={
        pageData.cmlLearningObjectives && (
          <TogglableDescription description={pageData.description} trackingName="syllabus_description" />
        )
      }
      syllabusItems={pageData.material.weeks.map((module, index) => {
        return (
          // eslint-disable-next-line no-restricted-syntax
          <SyllabusItem toggleButtonLabel={_t('Module details')} key={module.id} itemIndex={index + 1}>
            <SyllabusItemTitle title={module.name} />
            {/* @ts-expect-error Duration: string */}
            <ModuleItemSubtitle moduleIndex={index + 1} totalContentDuration={module.totalDuration} />
            <ModuleItemContent
              description={module.description}
              lectures={module.lectures}
              supplements={module.supplements}
              assessments={getGroupedAssessments(module.assessments)}
              showLecturePreview
              courseSlug={pageData.slug}
              authoringPartnerID={pageData.partners?.[0]?.id}
            />
          </SyllabusItem>
        );
      })}
      sidebar={
        <InstructorAndPartnerInfo
          averageInstructorRating={pageData.averageInstructorRating}
          instructorRatingCount={pageData.instructorRatingCount}
          instructors={pageData.instructors}
          numberOfInstructorsToShow={getNumberOfInstructorsToShow(
            pageData.instructors?.length,
            pageData.material?.weeks?.length
          )}
          partners={pageData.partners}
          embeddedContentPartners={getUniqueEmbeddedPartners(pageData.material?.weeks, pageData.partners)}
          trackingLocation="syllabus"
          disableCTA={config?.disableCta}
        />
      }
      footer={
        <>
          <CareerCertificateItem />
          {!config?.disableDegrees && (
            <EligibleDegreesItem
              degrees={pageData.degrees}
              partners={pageData.partners}
              slug={pageData.slug}
              productTags={undefined}
              disableCta={config?.disableCta}
              disableAceCta={config?.disableAceCta}
            />
          )}
        </>
      }
    />
  );
};

export default CourseSyllabusModuleRenderer;
